import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// ============================================================================
// TenantLogin
//
// Shown instead of <LogIn /> when the selected client code is "waralsadmin".
// Talks to the UniversalLogin API and stores the same sessionStorage keys the
// admin panel already relies on (token, userRole, objOutput, ...).
// ============================================================================

const API_URL = 'https://wsmsv1.aidigitalsec.com/api';
const TENANT_CLIENT_CODE = 'waralsadmin';

const BOOT_LINES = [
  { text: 'starting tenant control node', tag: 'ok' },
  { text: 'loading admin console modules', tag: 'ok' },
  { text: 'opening session channel', tag: 'ok' },
  { text: 'waiting for operator credentials', tag: 'wait' },
];

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------
function decodeJwt(token) {
  try {
    const clean = token.replace(/^Bearer\s+/i, '');
    const payload = clean.split('.')[1];
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json);
  } catch {
    return {};
  }
}

// Same keys and formats the previous waralsadmin login flow wrote.
function persistSession({ token, refreshToken, lastLoginOn, leftDays, adminUser }) {
  const put = (k, v) => sessionStorage.setItem(k, JSON.stringify(v));

  sessionStorage.setItem('login', true);
  sessionStorage.setItem('token', token);
  if (refreshToken) sessionStorage.setItem('refreshToken', refreshToken);
  if (lastLoginOn != null) sessionStorage.setItem('lastLoginOn', lastLoginOn);
  if (leftDays != null) sessionStorage.setItem('leftday', leftDays);

  const claims = decodeJwt(token);
  const role = claims.role || claims.Role ||
    claims['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] || null;
  if (role) sessionStorage.setItem('userRole', role);

  sessionStorage.setItem('objOutput', JSON.stringify(adminUser));
  put('userId', adminUser.userID);
  put('classId', adminUser.classId);
  put('sectionId', adminUser.sectionId);
  put('employeeId', adminUser.employeeID);
  put('userGroupId', adminUser.userGroupID);
  put('userType', adminUser.userType);
  put('organizationName', adminUser.organizationName);
  put('logo', adminUser.logo);
  put('clientImage', adminUser.imageUrl);
  put('clientName', adminUser.userName);
  put('expirydate', adminUser.expiryDate);
  put('branchId', adminUser.locationID);
  put('is_Master', adminUser.is_Master);
}

function redirectFor(u) {
  const isAdmin = u.userType === 'SuperAdmin' || (u.userType === 'Employee' && u.userGroupID === 1);
  return isAdmin ? '/admin/tenants' : '/dashboard';
}

// ---------------------------------------------------------------------------
// background: falling glyphs on a canvas (one draw loop, ~16 fps, DPR-aware)
// ---------------------------------------------------------------------------
function GlyphRain() {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext('2d');
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const glyphs = 'ｱｲｳｴｵｶｷｸｹｺｻｼｽｾｿ0123456789ABCDEF<>/{}[]#$%&*+='.split('');
    const size = 16;
    let w = 0;
    let h = 0;
    let drops = [];
    let raf = 0;
    let last = 0;

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = window.innerWidth;
      h = window.innerHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.fillStyle = '#050a08';
      ctx.fillRect(0, 0, w, h);
      drops = Array.from({ length: Math.ceil(w / size) }, () => -Math.random() * (h / size));
    };

    const draw = (t) => {
      raf = requestAnimationFrame(draw);
      if (t - last < 60) return;
      last = t;
      ctx.fillStyle = 'rgba(5,10,8,0.14)';
      ctx.fillRect(0, 0, w, h);
      ctx.font = `${size}px monospace`;
      for (let i = 0; i < drops.length; i += 1) {
        const y = drops[i] * size;
        ctx.fillStyle = Math.random() > 0.975 ? '#d7ffe6' : 'rgba(57,255,120,0.7)';
        ctx.fillText(glyphs[Math.floor(Math.random() * glyphs.length)], i * size, y);
        if (y > h && Math.random() > 0.975) drops[i] = 0;
        drops[i] += 1;
      }
    };

    resize();
    window.addEventListener('resize', resize);
    if (!reduce) raf = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return <canvas ref={ref} className="tl-rain" aria-hidden="true" />;
}

// isolated so the per-second tick doesn't re-render the form
function Clock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return <span>{now.toISOString().slice(11, 19)} utc</span>;
}

const CSS = `
.tl-root{--g:#3dff7f;--g-rgb:61,255,127;--amber:#ffb454;--red:#ff5c5c;--cyan:#4df3ff;--dim:#5f8f72;--line:#17683a;--bg:#050a08;--text:#d7ffe6;
  position:relative;min-height:100vh;display:flex;align-items:center;justify-content:center;padding:24px 16px;overflow:hidden;
  background:var(--bg);color:var(--text);font-family:"JetBrains Mono","Fira Code",ui-monospace,SFMono-Regular,Consolas,"Liberation Mono",monospace;font-size:13px}
.tl-root *{box-sizing:border-box}

/* ---- background layers ---- */
.tl-rain{position:fixed;inset:0;opacity:.8;pointer-events:none}
.tl-grid{position:fixed;inset:0;pointer-events:none;
  background-image:linear-gradient(rgba(var(--g-rgb),.06) 1px,transparent 1px),linear-gradient(90deg,rgba(var(--g-rgb),.06) 1px,transparent 1px);
  background-size:44px 44px;-webkit-mask-image:radial-gradient(ellipse at center,#000 20%,transparent 75%);mask-image:radial-gradient(ellipse at center,#000 20%,transparent 75%)}
.tl-vignette{position:fixed;inset:0;pointer-events:none;background:radial-gradient(ellipse at center,transparent 35%,rgba(0,0,0,.85) 100%)}
.tl-scan{position:fixed;left:0;right:0;top:0;height:120px;pointer-events:none;
  background:linear-gradient(180deg,transparent,rgba(var(--g-rgb),.07),transparent);animation:tl-sweep 7s linear infinite}
@keyframes tl-sweep{from{transform:translateY(-140px)}to{transform:translateY(100vh)}}

/* ---- card ---- */
.tl-card{position:relative;z-index:2;width:min(460px,100%);background:rgba(5,10,8,.86);-webkit-backdrop-filter:blur(6px);backdrop-filter:blur(6px);
  border:1px solid var(--line);border-radius:4px;box-shadow:0 0 0 1px rgba(var(--g-rgb),.05),0 0 48px rgba(var(--g-rgb),.12);animation:tl-glow 4s ease-in-out infinite}
@keyframes tl-glow{50%{box-shadow:0 0 0 1px rgba(var(--g-rgb),.1),0 0 70px rgba(var(--g-rgb),.22)}}
.tl-card.is-denied{border-color:var(--red);animation:tl-shake .45s}
.tl-card.is-granted{border-color:var(--g)}
@keyframes tl-shake{20%{transform:translateX(-9px)}40%{transform:translateX(8px)}60%{transform:translateX(-5px)}80%{transform:translateX(3px)}}
.tl-corner{position:absolute;width:14px;height:14px;border:2px solid var(--g);pointer-events:none}
.tl-corner--tl{top:-2px;left:-2px;border-right:0;border-bottom:0}
.tl-corner--tr{top:-2px;right:-2px;border-left:0;border-bottom:0}
.tl-corner--bl{bottom:-2px;left:-2px;border-right:0;border-top:0}
.tl-corner--br{bottom:-2px;right:-2px;border-left:0;border-top:0}

.tl-bar{display:flex;align-items:center;gap:8px;padding:9px 14px;font-size:11px;color:var(--dim);border-bottom:1px solid var(--line)}
.tl-dot{width:8px;height:8px;border-radius:50%;background:var(--g);box-shadow:0 0 8px var(--g);animation:tl-blink 1.6s steps(2,jump-none) infinite}
.tl-bar__spacer{margin-left:auto}
@keyframes tl-blink{50%{opacity:.25}}

.tl-body{padding:22px 24px 20px}

/* ---- title (the one loud element) ---- */
.tl-title{position:relative;margin:0 0 4px;font-size:27px;font-weight:700;letter-spacing:.02em;color:var(--g);text-shadow:0 0 14px rgba(var(--g-rgb),.55)}
.tl-title::before,.tl-title::after{content:attr(data-text);position:absolute;inset:0}
.tl-title::before{color:var(--red);left:2px;clip-path:inset(0 0 100% 0);animation:tl-g1 4.2s steps(1) infinite}
.tl-title::after{color:var(--cyan);left:-2px;clip-path:inset(100% 0 0 0);animation:tl-g2 3.6s steps(1) infinite}
@keyframes tl-g1{0%,91%,100%{clip-path:inset(0 0 100% 0)}92%{clip-path:inset(12% 0 58% 0)}95%{clip-path:inset(46% 0 24% 0)}97%{clip-path:inset(0 0 100% 0)}}
@keyframes tl-g2{0%,86%,100%{clip-path:inset(100% 0 0 0)}88%{clip-path:inset(62% 0 8% 0)}91%{clip-path:inset(20% 0 52% 0)}93%{clip-path:inset(100% 0 0 0)}}
.tl-sub{margin:0 0 16px;color:var(--dim);font-size:12px}

/* ---- boot log ---- */
.tl-boot{margin:0 0 20px;padding:10px 12px;min-height:88px;font-size:11.5px;line-height:1.75;background:rgba(0,0,0,.4);border:1px solid var(--line);border-radius:3px}
.tl-boot div{white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.tl-boot .ok{color:var(--g)}
.tl-boot .wait{color:var(--amber)}
.tl-boot .k{color:var(--dim)}
.tl-cursor{display:inline-block;width:7px;height:13px;margin-left:3px;vertical-align:-2px;background:var(--g);animation:tl-blink 1s steps(2,jump-none) infinite}

/* ---- fields ---- */
.tl-field{position:relative;margin-bottom:18px}
.tl-label{position:absolute;top:-8px;left:10px;z-index:1;padding:0 5px;font-size:10.5px;line-height:1;color:var(--g);background:#050a08}
.tl-input-wrap{display:flex;align-items:center;border:1px solid var(--line);border-radius:3px;transition:border-color .15s,box-shadow .15s}
.tl-input-wrap:focus-within{border-color:var(--g);box-shadow:0 0 0 1px rgba(var(--g-rgb),.35),0 0 18px rgba(var(--g-rgb),.18)}
.tl-prompt{padding:0 0 0 12px;color:var(--g)}
.tl-input{flex:1;min-width:0;padding:13px 10px;font:inherit;font-size:14px;color:var(--text);background:transparent;border:0;outline:none;caret-color:var(--g)}
.tl-input::placeholder{color:#3c6650}
.tl-input:disabled{opacity:.5}
.tl-input:-webkit-autofill{-webkit-text-fill-color:var(--text);transition:background-color 9999s}
.tl-peek{margin-right:8px;padding:4px 8px;font:inherit;font-size:11px;color:var(--dim);background:transparent;border:1px solid var(--line);border-radius:3px;cursor:pointer}
.tl-peek:hover,.tl-peek:focus-visible{color:var(--g);border-color:var(--g);outline:none}
.tl-note{margin:-10px 0 14px;font-size:11px;color:var(--amber)}

/* ---- status + button ---- */
.tl-msg{margin:0 0 14px;padding:8px 12px;font-size:12px;border-radius:3px}
.tl-msg--err{color:var(--red);border:1px solid var(--red);background:rgba(255,92,92,.07)}
.tl-msg--ok{color:var(--g);border:1px solid var(--g);background:rgba(var(--g-rgb),.08);animation:tl-flash .9s ease-out}
@keyframes tl-flash{0%{box-shadow:0 0 0 rgba(var(--g-rgb),0)}30%{box-shadow:0 0 36px rgba(var(--g-rgb),.7)}100%{box-shadow:0 0 0 rgba(var(--g-rgb),0)}}

.tl-btn{position:relative;width:100%;padding:13px 16px;overflow:hidden;font:inherit;font-size:13.5px;font-weight:700;color:#04140a;background:var(--g);border:1px solid var(--g);border-radius:3px;cursor:pointer;transition:box-shadow .15s,transform .05s}
.tl-btn:hover:not(:disabled),.tl-btn:focus-visible{outline:none;box-shadow:0 0 26px rgba(var(--g-rgb),.55)}
.tl-btn:active:not(:disabled){transform:translateY(1px)}
.tl-btn::after{content:"";position:absolute;top:0;bottom:0;width:60px;left:-80px;background:linear-gradient(100deg,transparent,rgba(255,255,255,.55),transparent);animation:tl-shine 3.6s ease-in-out infinite}
@keyframes tl-shine{60%,100%{left:120%}}
.tl-btn:disabled{cursor:progress;color:var(--g);background:transparent;opacity:1}
.tl-btn:disabled::after{display:none}
.tl-progress{position:relative;height:2px;margin-top:10px;overflow:hidden;background:rgba(var(--g-rgb),.12);opacity:0;transition:opacity .15s}
.tl-progress.on{opacity:1}
.tl-progress i{position:absolute;top:0;bottom:0;width:38%;background:var(--g);box-shadow:0 0 10px var(--g);animation:tl-slide 1s ease-in-out infinite}
@keyframes tl-slide{from{left:-40%}to{left:100%}}

.tl-foot{display:flex;justify-content:space-between;gap:12px;padding:10px 14px;font-size:10.5px;color:var(--dim);border-top:1px solid var(--line)}

@media (max-width:480px){.tl-title{font-size:22px}.tl-body{padding:18px 16px 16px}}
@media (prefers-reduced-motion:reduce){.tl-root *,.tl-root *::before,.tl-root *::after{animation:none!important;transition:none!important}}
`;

// ---------------------------------------------------------------------------
// page
// ---------------------------------------------------------------------------
const TenantLogin = () => {
  const navigate = useNavigate();
  const timer = useRef(null);
  const [userId, setUserId] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [caps, setCaps] = useState(false);
  const [phase, setPhase] = useState('idle'); // idle | auth | granted | denied
  const [error, setError] = useState('');
  const [booted, setBooted] = useState(0);
  const [sessionTag] = useState(() => Math.random().toString(16).slice(2, 10));

  const clientCode = (sessionStorage.getItem('clientCode') || '').trim();
  const isTenantClient = clientCode.toLowerCase() === TENANT_CLIENT_CODE;

  // this screen is only for the waralsadmin client; everyone else gets the normal login
  useEffect(() => {
    if (!isTenantClient) navigate('/login', { replace: true });
  }, [isTenantClient, navigate]);

  // boot log: one line at a time
  useEffect(() => {
    if (booted >= BOOT_LINES.length) return undefined;
    const id = setTimeout(() => setBooted((n) => n + 1), booted === 0 ? 350 : 480);
    return () => clearTimeout(id);
  }, [booted]);

  useEffect(() => () => clearTimeout(timer.current), []);

  const deny = (msg) => {
    setError(msg);
    setPhase('denied');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (phase === 'auth' || phase === 'granted') return;
    if (!userId.trim() || !password) {
      deny('Enter both the operator ID and the passkey.');
      return;
    }

    setError('');
    setPhase('auth');

    try {
      const response = await fetch(`${API_URL}/Default/UniversalLogin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: sessionStorage.getItem('token1'),
        },
        body: JSON.stringify({ userId, password }),
      });

      if (!response.ok) {
        let msg = 'Invalid credentials.';
        try {
          const errData = await response.json();
          if (errData.msg) msg = errData.msg;
        } catch (parseErr) {
          console.error('Failed to parse error response:', parseErr);
        }
        deny(msg);
        return;
      }

      const data = await response.json();
      const { token, refreshToken, lastLoginOn, leftDays, adminUser } = data;

      if (!token || !adminUser || typeof adminUser !== 'object') {
        console.error('Unexpected login response shape:', data);
        deny('The server returned an unexpected response.');
        return;
      }

      persistSession({ token, refreshToken, lastLoginOn, leftDays, adminUser });
      setPhase('granted');
      timer.current = setTimeout(() => navigate(redirectFor(adminUser)), 900);
    } catch (err) {
      console.error('API request error:', err);
      deny('Could not reach the server. Check the connection and try again.');
    }
  };

  if (!isTenantClient) return null;

  const busy = phase === 'auth' || phase === 'granted';

  return (
    <div className="tl-root">
      <style>{CSS}</style>
      <GlyphRain />
      <div className="tl-grid" aria-hidden="true" />
      <div className="tl-scan" aria-hidden="true" />
      <div className="tl-vignette" aria-hidden="true" />

      <div className={`tl-card${phase === 'denied' ? ' is-denied' : ''}${phase === 'granted' ? ' is-granted' : ''}`}>
        <i className="tl-corner tl-corner--tl" />
        <i className="tl-corner tl-corner--tr" />
        <i className="tl-corner tl-corner--bl" />
        <i className="tl-corner tl-corner--br" />

        <div className="tl-bar">
          <span className="tl-dot" />
          <span>tenant_console</span>
          <span className="tl-bar__spacer"><Clock /></span>
        </div>

        <div className="tl-body">
          <h1 className="tl-title" data-text="tenant_console">tenant_console</h1>
          <p className="tl-sub">Sign in to manage tenants and their databases.</p>

          <div className="tl-boot" aria-hidden="true">
            {BOOT_LINES.slice(0, booted).map((l) => (
              <div key={l.text}>
                <span className={l.tag}>[{l.tag === 'ok' ? ' ok ' : ' .. '}]</span> <span className="k">{l.text}</span>
              </div>
            ))}
            <div>
              <span className="k">{`target: ${clientCode.toLowerCase()}`}</span>
              <span className="tl-cursor" />
            </div>
          </div>

          <form onSubmit={handleSubmit} noValidate>
            <div className="tl-field">
              <label className="tl-label" htmlFor="tl-user">operator_id</label>
              <div className="tl-input-wrap">
                <span className="tl-prompt">&gt;</span>
                <input
                  id="tl-user"
                  className="tl-input"
                  type="text"
                  name="userId"
                  autoComplete="username"
                  autoFocus
                  spellCheck={false}
                  placeholder="username"
                  value={userId}
                  disabled={busy}
                  onChange={(e) => setUserId(e.target.value)}
                />
              </div>
            </div>

            <div className="tl-field">
              <label className="tl-label" htmlFor="tl-pass">passkey</label>
              <div className="tl-input-wrap">
                <span className="tl-prompt">&gt;</span>
                <input
                  id="tl-pass"
                  className="tl-input"
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  autoComplete="current-password"
                  placeholder="password"
                  value={password}
                  disabled={busy}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyUp={(e) => setCaps(!!e.getModifierState && e.getModifierState('CapsLock'))}
                />
                <button
                  type="button"
                  className="tl-peek"
                  onClick={() => setShowPassword((s) => !s)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? 'hide' : 'show'}
                </button>
              </div>
            </div>
            {caps && <p className="tl-note">! caps lock is on</p>}

            {phase === 'denied' && <div className="tl-msg tl-msg--err" role="alert">! access denied: {error}</div>}
            {phase === 'granted' && <div className="tl-msg tl-msg--ok" role="status">&gt; access granted. opening console...</div>}

            <button type="submit" className="tl-btn" disabled={busy}>
              {phase === 'auth' ? 'authenticating...' : phase === 'granted' ? 'access granted' : 'sign_in'}
            </button>
            <div className={`tl-progress${phase === 'auth' ? ' on' : ''}`}><i /></div>
          </form>
        </div>

        <div className="tl-foot">
          <span>authorised operators only</span>
          <span>session {sessionTag}</span>
        </div>
      </div>
    </div>
  );
};

export default TenantLogin;